import { Minus, Plus, RotateCcw, Anchor, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FloorPlanZoomControlsProps {
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  onResetView: () => void;
  minZoom?: number;
  maxZoom?: number;
  onAnchorView?: () => void;
  isAnchoring?: boolean;
}

export const FloorPlanZoomControls = ({
  zoomLevel,
  onZoomChange,
  onResetView,
  minZoom = 0.1,
  maxZoom = 2,
  onAnchorView,
  isAnchoring = false,
}: FloorPlanZoomControlsProps) => {
  const zoomPercentage = Math.round(zoomLevel * 100);

  const handleZoomIn = () => {
    onZoomChange(Math.min(zoomLevel + 0.25, maxZoom));
  };

  const handleZoomOut = () => {
    onZoomChange(Math.max(zoomLevel - 0.25, minZoom));
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 bg-background/80 backdrop-blur-sm rounded-lg border border-border p-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleZoomOut}
              disabled={zoomLevel <= minZoom}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom arrière</TooltipContent>
        </Tooltip>

        <div className="flex items-center gap-2 w-32">
          <Slider
            value={[zoomLevel]}
            onValueChange={([value]) => onZoomChange(value)}
            min={minZoom}
            max={maxZoom}
            step={0.05}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-10 text-right">
            {zoomPercentage}%
          </span>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleZoomIn}
              disabled={zoomLevel >= maxZoom}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom avant</TooltipContent>
        </Tooltip>

        <div className="w-px h-4 bg-border" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onResetView}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Réinitialiser la vue</TooltipContent>
        </Tooltip>

        {onAnchorView && (
          <>
            <div className="w-px h-4 bg-border" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onAnchorView}
                  disabled={isAnchoring}
                >
                  {isAnchoring ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Anchor className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Ancrer cette vue (tous les appareils)</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
